
import styles from '../style.module.css';
import {useSelector} from "react-redux";
import React, {useEffect, useRef} from "react";
import { motion, useReducedMotion } from "framer-motion";
import bannerImage from 'assets/about_us_banner.png';
import lang from "../../../lang/lang";
import langData from "../../../lang/data";
import UseOutSideClick from "helpers/UseOutSideClick";
import FacebookSvg from "../../../assets/SVG/FacebookSvg";
import InstagramSvg from "../../../assets/SVG/InstagramSvg";
import WhatsappSvg from "../../../assets/SVG/WhatsappSvg";
import ViberSvg from "../../../assets/SVG/ViberSvg";
import BannerRotateItem from './BannerRotateItem'

export default function MainBanner({banner}) {

    const {currentLanguage} = useSelector(({main}) => main);
    const reduceMotion = useReducedMotion();

    const [showPhones, setShowPhones] = React.useState(false);

    const openedPhonesRef = useRef(null);
    const bannerRef = useRef(null);
    const bgRef = useRef(null);
    const contentRef = useRef(null);
    const cardsRef = useRef(null);
    const storesRef = useRef(null);

    UseOutSideClick(openedPhonesRef, () => setShowPhones(false));

    const logoSrc =
        currentLanguage === "hy"
            ? "https://vlv.am/public/logos/header_hy_night.svg"
            : "https://vlv.am/public/logos/header_ru_night.svg";

    const t = langData.titles;

    useEffect(() => {
        let rafId = 0;

        const updateParallax = () => {
            const section = bannerRef.current;
            if (!section || reduceMotion) return;

            const isMobile = window.innerWidth <= 768;
            if (isMobile) {
                if (bgRef.current) bgRef.current.style.transform = "scale(1.04)";
                if (contentRef.current) contentRef.current.style.transform = "";
                if (cardsRef.current) cardsRef.current.style.transform = "";
                if (storesRef.current) storesRef.current.style.transform = "";
                return;
            }

            const rect = section.getBoundingClientRect();
            const viewH = window.innerHeight || 1;
            const centerOffset = viewH / 2 - (rect.top + rect.height / 2);
            const progress = Math.max(-1, Math.min(1, centerOffset / viewH));

            if (bgRef.current) {
                bgRef.current.style.transform = `translate3d(0, ${progress * 48}px, 0) scale(1.08)`;
            }
            if (contentRef.current) {
                contentRef.current.style.transform = `translate3d(0, ${progress * 18}px, 0)`;
            }
            if (cardsRef.current) {
                cardsRef.current.style.transform = `translate3d(-50%, calc(-50% + ${progress * -28}px), 0)`;
            }
            if (storesRef.current) {
                storesRef.current.style.transform = `translate3d(0, ${progress * -10}px, 0)`;
            }
        };

        const onScroll = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(updateParallax);
        };

        window.addEventListener("scroll", onScroll, {passive: true});
        window.addEventListener("resize", onScroll);
        updateParallax();

        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
            cancelAnimationFrame(rafId);
        };
    }, [reduceMotion]);

    const textMotion = reduceMotion
        ? {}
        : {
            initial: { opacity: 0, y: 28 },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
        };

    return (
        <div className={styles.main_banner} ref={bannerRef}>
            <div className={styles.banner_bg_wrap}>
                <div className={styles.banner_bg_parallax} ref={bgRef}>
                    <img
                        alt={'about page banner'}
                        className={`${styles.banner_bg} ${reduceMotion ? "" : styles.banner_bg_ken}`}
                        src={bannerImage}
                    />
                </div>
                <div className={styles.banner_overlay} />
                <div className={styles.banner_glow} />

                <div className={styles.banner_content}>
                    <div className={styles.banner_header}>
                        <button
                            className={styles.banner_header_button}
                            onClick={() => window.history.back()}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="6" height="11" viewBox="0 0 6 11" fill="none">
                                <path d="M5.24996 0.75L0.75 5.25002L5.25 9.75002" stroke="#282828" strokeWidth="1.5"
                                      strokeMiterlimit="16" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            {lang[currentLanguage].back}
                        </button>
                        <div className={styles.about_logo_wrap}>
                            <motion.img
                                src={logoSrc}
                                alt={'VLV'}
                                className={styles.about_logo}
                                initial={reduceMotion ? false : { opacity: 0, y: -12, scale: 0.94 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                            />
                        </div>
                        <div className={styles.banner_header_spacer} aria-hidden="true" />
                        <div className={styles.phone_social_box}>
                            <div className={styles.phones} ref={openedPhonesRef}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path
                                        d="M10.2058 11.3733C10.866 11.4093 11.375 10.8715 11.375 10.2514V8.82728C11.3749 8.18427 10.8632 7.73009 10.3197 7.65198C9.84871 7.58437 9.40696 7.43784 9.1122 7.32205C8.69424 7.1579 8.18382 7.22795 7.83534 7.57644L6.2807 9.13108C4.56354 8.22155 3.15277 6.81099 2.24337 5.09371L3.79855 3.53968C4.14705 3.19117 4.2171 2.68078 4.05293 2.26277C3.93719 1.96804 3.79064 1.5263 3.72301 1.05529C3.64489 0.511776 3.19071 0.000105 2.54774 0H1.12365C0.503518 0 -0.0343042 0.509034 0.00171078 1.16915C0.302769 6.66749 4.70752 11.0722 10.2058 11.3733Z"
                                        fill="white"/>
                                </svg>
                                <a
                                    href="tel:010349944"
                                    className={styles.phoneBtn}
                                >
                                    010-34-99-44
                                </a>
                                <svg
                                    onClick={() => setShowPhones(!showPhones)}
                                    xmlns="http://www.w3.org/2000/svg" width="8" height="5" viewBox="0 0 8 5" fill="none"
                                >
                                    <path d="M0.5 0.500029L4.00002 4L7.50002 0.5" stroke="white" strokeMiterlimit="16"
                                          strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                {
                                    showPhones && (
                                        <div className={styles.phonesOpened}>
                                            <a className={styles.phoneBtn} href="tel:010349944">010-34-99-44</a>
                                            <a className={styles.phoneBtn} href="tel:010449968">010-44-99-68</a>
                                        </div>
                                    )
                                }
                            </div>
                            <div className={styles.vertical_line} />
                            <div className={styles.social_links}>
                                <a
                                    href="https://www.facebook.com/people/VLV-Electronics/100064824965577/?mibextid=LQQJ4d"
                                    aria-label="facebook"
                                    className={styles.socLinks}
                                >
                                    <FacebookSvg className={styles.socIcon}/>
                                </a>
                                <a
                                    href="https://www.instagram.com/vlv_centre/"
                                    aria-label="instagram"
                                    className={styles.socLinks}
                                >
                                    <InstagramSvg className={styles.socIcon}/>
                                </a>
                                <a
                                    href="https://wa.me/37491296655" aria-label="whatsapp"
                                    className={styles.socLinks}
                                >
                                    <WhatsappSvg className={styles.socIcon}/>
                                </a>
                                <a
                                    href="viber://chat?number=+37491296655/"
                                    aria-label="viber"
                                    className={styles.socLinks}
                                >
                                    <ViberSvg className={styles.socIcon}/>
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className={styles.banner_title_box} ref={contentRef}>
                        <motion.p
                            className={styles.banner_brand}
                            {...textMotion}
                            transition={{ ...(textMotion.transition || {}), delay: 0.08 }}
                        >
                            {t.aboutHeroBrand[currentLanguage]}
                        </motion.p>
                        <motion.p
                            className={styles.banner_main_title}
                            {...textMotion}
                            transition={{ ...(textMotion.transition || {}), delay: 0.18 }}
                        >
                            {t.aboutHeroTitle[currentLanguage]}
                        </motion.p>
                        <motion.p
                            className={styles.banner_subtitle}
                            {...textMotion}
                            transition={{ ...(textMotion.transition || {}), delay: 0.3 }}
                        >
                            {t.aboutHeroSubtitle[currentLanguage]}
                        </motion.p>
                    </div>

                    <div className={styles.banner_bottom}>
                        <div className={styles.banner_mobile_stats}>
                            <div className={styles.banner_mobile_stat}>
                                <strong>22+</strong>
                                <span>{t.aboutHeroStatYears[currentLanguage]}</span>
                            </div>
                            <div className={styles.banner_mobile_stat}>
                                <strong>10 000+</strong>
                                <span>{t.aboutHeroStatProducts[currentLanguage]}</span>
                            </div>
                            <div className={styles.banner_mobile_stat}>
                                <strong>150+</strong>
                                <span>{t.aboutHeroStatBrands[currentLanguage]}</span>
                            </div>
                            <div className={styles.banner_mobile_stat}>
                                <strong>600+</strong>
                                <span>{t.aboutHeroStatEmployees[currentLanguage]}</span>
                            </div>
                        </div>
                        <div className={styles.store_links} ref={storesRef}>
                            <img
                                className={styles.ws_download_image}
                                src="https://vlv.am/public/images/download-apple-logo.svg"
                                alt="Download on the App Store"
                                onClick={() =>
                                    window.open('https://apps.apple.com/am/app/vlv-centre/id6476923086', '_blank', 'noopener,noreferrer')
                                }
                            />
                            <img
                                className={styles.ws_download_image}
                                src="https://vlv.am/public/images/download-google-play.svg"
                                alt="Get it on Google Play"
                                onClick={() =>
                                    window.open(
                                        'https://play.google.com/store/apps/details?id=com.vlv&pcampaignid=web_share',
                                        '_blank',
                                        'noopener,noreferrer'
                                    )
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.rotate_images_box} ref={cardsRef}>
                <BannerRotateItem
                    value={'22+'}
                    text={t.aboutHeroStatYears[currentLanguage]}
                    background={'rgba(255,255,255,0.94)'}
                    valueColor={'#111111'}
                    textColor={'#4a4a4a'}
                    rotate={-6}
                    delay={0.25}
                    index={0}
                />
                <BannerRotateItem
                    value={'10000+'}
                    text={t.aboutHeroStatProducts[currentLanguage]}
                    background={'#141414'}
                    valueColor={'#ffffff'}
                    textColor={'rgba(255,255,255,0.78)'}
                    rotate={4}
                    delay={0.4}
                    index={1}
                />
                <BannerRotateItem
                    value={'150+'}
                    text={t.aboutHeroStatBrands[currentLanguage]}
                    background={'rgba(255,255,255,0.94)'}
                    valueColor={'#111111'}
                    textColor={'#4a4a4a'}
                    rotate={-4}
                    delay={0.55}
                    index={2}
                />
                <BannerRotateItem
                    value={'600+'}
                    text={t.aboutHeroStatEmployees[currentLanguage]}
                    background={'#141414'}
                    valueColor={'#ffffff'}
                    textColor={'rgba(255,255,255,0.78)'}
                    rotate={5}
                    delay={0.7}
                    index={3}
                />
            </div>
        </div>
    )
}
